import { SafeHtml } from "~/hec/components/atoms/safe-html/safe-html";
import { FeatureLink } from "~/hec/components/molecules/feature-link/feature-link";
import { useFeatureLinkTarget } from "~/hec/hooks/use-feature-link-target";
import { useHeadingFontClass } from "~/hec/hooks/use-heading-font-class";
import {
  builderImageSrcSet,
  builderImageUrl,
  builderImageWidths,
} from "~/hec/utils/builder-image-url";
import { cn } from "~/lib/utils";

import type { FeatureHeroAlign, FeatureHeroProps } from "./types";

// Breakpoints are Builder's (small ≤639, medium ≤991), not Tailwind's, so the
// component lays out exactly like the symbol it replaces.
export const FEATURE_HERO_TEXT_BOX_BACKGROUND = "rgba(255, 255, 255, 0.9)";

const BOX_ALIGN: Record<FeatureHeroAlign, string> = {
  left: "min-[640px]:ml-[5%] min-[640px]:mr-auto",
  center: "min-[640px]:mx-auto",
  right: "min-[640px]:ml-auto min-[640px]:mr-[5%]",
};

const TEXT_ALIGN: Record<FeatureHeroAlign, string> = {
  left: "min-[640px]:text-left",
  center: "min-[640px]:text-center",
  right: "min-[640px]:text-right",
};

// Logo and button follow the text alignment.
const ITEM_ALIGN: Record<FeatureHeroAlign, string> = {
  left: "min-[640px]:ml-[0px] min-[640px]:mr-auto",
  center: "min-[640px]:mx-auto",
  right: "min-[640px]:ml-auto min-[640px]:mr-[0px]",
};

const LOGO_POSITION: Record<FeatureHeroAlign, string> = {
  left: "min-[640px]:object-left",
  center: "min-[640px]:object-center",
  right: "min-[640px]:object-right",
};

// The background spans the hero (up to 1440px); mobile is the viewport width.
const DESKTOP_WIDTHS = builderImageWidths(1440);
const MOBILE_WIDTHS = builderImageWidths(390);
const LOGO_WIDTHS = builderImageWidths(260);
const BADGE_WIDTHS = builderImageWidths(100);

/**
 * Organism: the "Hero" symbol (COM-945) as a component. The whole hero is one
 * link; the button inside it is only visual, so there is no nested link. The
 * logo and badge are left out of the DOM when blank. `headingHtml` arrives
 * with no `<a>` (the block strips them): the hero is the link. `priority`
 * loads the background eagerly with high fetch priority (above the fold).
 */
export function FeatureHero({
  link: linkTarget,
  desktopImage,
  mobileImage,
  eyebrow,
  logo,
  logoAlt,
  headingHtml,
  headingTag = "h2",
  headingFont,
  subheading,
  buttonText,
  badge,
  badgeAlt,
  textBoxAlign = "left",
  textAlign = "left",
  textBoxBackgroundColor,
  priority = true,
  className,
  ...rest
}: FeatureHeroProps) {
  const link = useFeatureLinkTarget(linkTarget);
  const headingFontClass = useHeadingFontClass(headingFont);

  return (
    <FeatureLink
      target={link}
      // `className` comes from Builder's Style tab, so an author's height or
      // min height lands on the hero itself and overrides these defaults.
      className={cn(
        "min-[640px]:h-[400px] min-[640px]:min-h-[440px] min-[992px]:min-h-0 relative flex h-[600px] w-full flex-col overflow-hidden",
        className,
      )}
      {...rest}
      data-testid="feature-hero-root"
    >
      {desktopImage || mobileImage ? (
        <picture>
          {mobileImage ? (
            <source
              media="(max-width: 639px)"
              srcSet={
                builderImageSrcSet(mobileImage, MOBILE_WIDTHS) ?? mobileImage
              }
            />
          ) : null}
          <img
            src={builderImageUrl(desktopImage || mobileImage, {
              width: 1600,
              format: "webp",
            })}
            srcSet={builderImageSrcSet(
              desktopImage || mobileImage,
              DESKTOP_WIDTHS,
            )}
            alt=""
            loading={priority ? "eager" : "lazy"}
            fetchPriority={priority ? "high" : undefined}
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover"
          />
        </picture>
      ) : null}
      <div className="min-[640px]:absolute min-[640px]:left-1/2 min-[640px]:top-1/2 min-[640px]:h-auto min-[640px]:max-w-[1440px] min-[640px]:-translate-x-1/2 min-[640px]:-translate-y-1/2 min-[640px]:items-stretch min-[992px]:flex-row relative flex h-full w-full flex-col justify-start">
        <div
          className={cn(
            "relative mx-auto mt-[16px] flex w-[calc(100%_-_16px)] flex-col gap-[8px] overflow-visible rounded-[24px] p-[32px] text-center",
            "min-[640px]:my-auto min-[640px]:h-fit min-[640px]:w-full min-[640px]:max-w-[60%] min-[640px]:justify-start",
            "min-[992px]:max-w-[40%] min-[992px]:p-[40px]",
            BOX_ALIGN[textBoxAlign],
            TEXT_ALIGN[textAlign],
          )}
          style={{
            backgroundColor:
              textBoxBackgroundColor || FEATURE_HERO_TEXT_BOX_BACKGROUND,
          }}
          data-testid="feature-hero-text-section"
        >
          {eyebrow ? (
            <p className="font-grotesque text-[14px] font-medium leading-[1.5] text-[#142745]">
              {eyebrow}
            </p>
          ) : null}
          {logo ? (
            <img
              src={builderImageUrl(logo, { width: 520, format: "webp" })}
              srcSet={builderImageSrcSet(logo, LOGO_WIDTHS)}
              sizes="(max-width: 639px) 123px, 260px"
              alt={logoAlt ?? ""}
              loading="lazy"
              decoding="async"
              className={cn(
                "min-[640px]:max-w-[260px] mx-auto h-[40px] w-full max-w-[123px] object-contain object-center",
                ITEM_ALIGN[textAlign],
                LOGO_POSITION[textAlign],
              )}
              data-testid="feature-hero-logo-image"
            />
          ) : null}
          {headingHtml ? (
            <SafeHtml
              as={headingTag}
              html={headingHtml}
              className={cn(
                "min-[640px]:text-[30px] min-[640px]:leading-[34px] min-[992px]:text-[32px] text-balance text-[26px] font-medium leading-[28px] text-emphasis",
                headingFontClass,
              )}
              data-testid="feature-hero-name"
            />
          ) : null}
          {subheading ? (
            <p className="min-[992px]:text-[20px] w-full text-balance font-grotesque text-base font-light leading-[1.5] text-[#142745]">
              {subheading}
            </p>
          ) : null}
          {buttonText ? (
            <span
              className={cn(
                // Looks like the primary Core:Button; the hero is the link.
                "mx-auto inline-flex h-[3.125rem] w-fit items-center justify-center rounded-[0.5rem] bg-primary px-[28px] pb-[20px] pt-[22px] font-grotesque text-base font-medium text-white",
                ITEM_ALIGN[textAlign],
              )}
              data-testid="feature-hero-button"
            >
              {buttonText}
            </span>
          ) : null}
          {badge ? (
            <img
              src={builderImageUrl(badge, { width: 200, format: "webp" })}
              srcSet={builderImageSrcSet(badge, BADGE_WIDTHS)}
              alt={badgeAlt ?? ""}
              loading="lazy"
              decoding="async"
              className="min-[640px]:bottom-auto min-[640px]:right-[-50px] min-[640px]:top-[-20px] min-[992px]:right-[-20px] absolute bottom-[-50px] right-[30px] w-[155px] max-w-[100px]"
              data-testid="feature-hero-badge"
            />
          ) : null}
        </div>
      </div>
    </FeatureLink>
  );
}
